import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Eye, Pencil } from "lucide-react";
import api from "@/lib/api";
import Markdown from "@/components/Markdown";

export default function AdminLegal() {
  const [docs, setDocs] = useState([]);
  const [slug, setSlug] = useState(null);
  const [form, setForm] = useState({ title: "", body: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    api.get("/admin/legal").then((r) => {
      setDocs(r.data);
      if (r.data.length) {
        setSlug(r.data[0].slug);
        setForm({ title: r.data[0].title, body: r.data[0].body });
      }
    }).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);

  const pick = (d) => {
    setSlug(d.slug);
    setForm({ title: d.title, body: d.body });
    setPreview(false);
  };

  const current = docs.find((d) => d.slug === slug);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/legal/${slug}`, { title: form.title, body: form.body });
      setDocs((ds) => ds.map((d) => (d.slug === slug ? { ...d, ...form, version: data.version, updated_at: new Date().toISOString() } : d)));
      toast.success(`Saved — version ${data.version}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-legal">
      <div className="mb-6">
        <p className="overline mb-2">Admin · Legal</p>
        <h1 className="font-editorial text-3xl font-medium tracking-tight text-white sm:text-4xl">Legal documents</h1>
        <p className="mt-2 max-w-2xl text-sm text-emerald-100/60">Edit your Terms, policies and agreements in Markdown. Changes publish live and are linked in the site footer. Saving bumps the version — the Affiliate Agreement version drives affiliate re-acceptance.</p>
      </div>

      {loading ? (
        <div className="card-edge p-8 text-emerald-100/50">Loading…</div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
          <div className="grid content-start gap-1" data-testid="admin-legal-tabs">
            {docs.map((d) => (
              <button
                key={d.slug}
                onClick={() => pick(d)}
                data-testid={`admin-legal-tab-${d.slug}`}
                className={`rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                  d.slug === slug ? "bg-emerald-500/12 font-medium text-lime-200" : "text-emerald-100/60 hover:bg-white/[0.04] hover:text-white"
                }`}
              >
                {d.title}
                <span className="ml-1 text-[10px] text-emerald-100/40">v{d.version}</span>
              </button>
            ))}
          </div>

          <div className="card-moss p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-emerald-100/60">Title</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  data-testid="admin-legal-title"
                  className="w-full rounded-md border border-emerald-500/15 bg-[#0d2416]/60 px-3 py-2 text-sm text-white focus:border-emerald-400/60 focus:outline-none"
                />
              </div>
              <button onClick={() => setPreview((p) => !p)} data-testid="admin-legal-preview-toggle" className="btn-edge-ghost mt-5 text-xs">
                {preview ? <><Pencil size={13} /> Edit</> : <><Eye size={13} /> Preview</>}
              </button>
            </div>

            {preview ? (
              <div className="min-h-[360px] rounded-lg border border-emerald-500/12 bg-[#0d2416]/40 p-5 text-[15px] text-emerald-50/90" data-testid="admin-legal-preview">
                <Markdown>{form.body}</Markdown>
              </div>
            ) : (
              <textarea
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                rows={20}
                data-testid="admin-legal-body"
                spellCheck={false}
                className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-4 py-3 font-mono text-[13px] leading-relaxed text-white focus:border-emerald-400/60 focus:outline-none"
              />
            )}

            <div className="mt-4 flex items-center justify-between gap-3">
              <p className="text-xs text-emerald-100/40">
                {current?.updated_at ? `Last updated ${new Date(current.updated_at).toLocaleString()}` : ""}
              </p>
              <button onClick={save} disabled={saving} data-testid="admin-legal-save" className="btn-edge text-sm">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save document
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
